import { useState, useEffect, useCallback } from 'react';
import { Divider } from '../../ui/Divider/Divider';
import { Toast } from '../../ui/Toast/Toast';
import Dropdown from '../../ui/Dropdown/Dropdown';
import CheckboxGroup from '../../ui/Checkbox/CheckboxGroup';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { setGeneralSettingsChanged } from '../../../store/slices/accountSlice';
import { AccountSave } from '../Account/AccountSave';
import { notificationService } from '../../../services/NotificationService';
import type { NotificationPreferencesPayload } from '../../../types/Notification.types';
import type { UserTwoFactorStatus } from '../../../types/User.types';
import {
  disableMyTwoFactor,
  getAvailableLanguages,
  getMySecuritySettings,
  getAvailableTimezones,
  getUserSettings,
  updateUserSettings
} from '../../../services/UserService';
import {
  PrimaryButton,
  TextButton,
  WhiteButton
} from '../../ui/Button/Buttons';
import { userManager } from '../../OidcAuthProvider';
import Modal from '../../ui/Modal/Modal';

const prefsToArray = (prefs: { email: boolean; inApp: boolean }): string[] => {
  const result: string[] = [];
  if (prefs.email) result.push('email');
  if (prefs.inApp) result.push('in-app');
  return result;
};

const formatTwoFactorMethods = (methods: string[]): string =>
  methods
    .map((method) => {
      if (method === 'otp') return 'Authenticator app';
      if (method === 'webauthn') return 'Security key';
      if (method === 'webauthn-passwordless')
        return 'Passwordless security key';
      return method;
    })
    .join(', ');

export default function GeneralSettings() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.account);

  const [showToast, setShowToast] = useState(false);
  const [toastText, setToastText] = useState('Changes successfully saved!');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSecurityLoading, setIsSecurityLoading] = useState(true);
  const [isTwoFactorUpdating, setIsTwoFactorUpdating] = useState(false);
  const [securityError, setSecurityError] = useState('');
  const [twoFactorStatus, setTwoFactorStatus] = useState<UserTwoFactorStatus>({
    twoFactorEnabled: false,
    twoFactorMethods: []
  });
  const [userActionNotifications, setUserActionNotifications] = useState<
    string[]
  >([]);
  const [informativeNotifications, setInformativeNotifications] = useState<
    string[]
  >([]);
  const [alertNotifications, setAlertNotifications] = useState<string[]>([]);
  const [successNotifications, setSuccessNotifications] = useState<string[]>(
    []
  );
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [languageOptions, setLanguageOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [timezoneOptions, setTimezoneOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [show2faModal, setShow2faModal] = useState(false);

  // Track initial values to detect changes
  const [initialValues, setInitialValues] = useState({
    userActionNotifications: [] as string[],
    informativeNotifications: [] as string[],
    alertNotifications: [] as string[],
    successNotifications: [] as string[],
    selectedLanguage: '',
    selectedTimezone: ''
  });

  // Fetch initial preferences and options on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prefs, languages, timezones, userSettings] = await Promise.all([
          notificationService.getNotificationPreferences(),
          getAvailableLanguages(),
          getAvailableTimezones(),
          getUserSettings()
        ]);

        const securitySettings = await getMySecuritySettings().catch(
          (error) => {
            console.error('Failed to fetch security settings:', error);
            setSecurityError(
              'Unable to load two-factor authentication status.'
            );
            return null;
          }
        );

        // Set dropdown options
        setLanguageOptions(languages);
        setTimezoneOptions(timezones);

        if (securitySettings) {
          setTwoFactorStatus(securitySettings);
          setSecurityError('');
        }

        // Set notification preferences
        const userAction = prefsToArray(prefs.userAction);
        const informative = prefsToArray(prefs.informative);
        const alert = prefsToArray(prefs.alert);
        const success = prefsToArray(prefs.success);

        setUserActionNotifications(userAction);
        setInformativeNotifications(informative);
        setAlertNotifications(alert);
        setSuccessNotifications(success);

        // Set language/timezone from user settings
        setSelectedLanguage(userSettings.language);
        setSelectedTimezone(userSettings.timezone);

        setInitialValues({
          userActionNotifications: userAction,
          informativeNotifications: informative,
          alertNotifications: alert,
          successNotifications: success,
          selectedLanguage: userSettings.language,
          selectedTimezone: userSettings.timezone
        });
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setIsLoading(false);
        setIsSecurityLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNotificationChange = (
    value: string,
    checked: boolean,
    currentValues: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (checked) {
      setter([...currentValues, value]);
    } else {
      const newValues = currentValues.filter((v) => v !== value);
      if (newValues.length > 0) {
        setter(newValues);
      }
    }
  };

  // Check if any values have changed
  const hasChanges = useCallback(() => {
    return (
      JSON.stringify(userActionNotifications) !==
        JSON.stringify(initialValues.userActionNotifications) ||
      JSON.stringify(informativeNotifications) !==
        JSON.stringify(initialValues.informativeNotifications) ||
      JSON.stringify(alertNotifications) !==
        JSON.stringify(initialValues.alertNotifications) ||
      JSON.stringify(successNotifications) !==
        JSON.stringify(initialValues.successNotifications) ||
      selectedLanguage !== initialValues.selectedLanguage ||
      selectedTimezone !== initialValues.selectedTimezone
    );
  }, [
    userActionNotifications,
    informativeNotifications,
    alertNotifications,
    successNotifications,
    selectedLanguage,
    selectedTimezone,
    initialValues
  ]);

  // Update generalSettingsChanged in store when values change
  useEffect(() => {
    dispatch(setGeneralSettingsChanged(hasChanges()));
  }, [hasChanges, dispatch]);

  const buildPreferencesPayload = (): NotificationPreferencesPayload => ({
    userAction: {
      email: userActionNotifications.includes('email'),
      inApp: userActionNotifications.includes('in-app')
    },
    informative: {
      email: informativeNotifications.includes('email'),
      inApp: informativeNotifications.includes('in-app')
    },
    alert: {
      email: alertNotifications.includes('email'),
      inApp: alertNotifications.includes('in-app')
    },
    success: {
      email: successNotifications.includes('email'),
      inApp: successNotifications.includes('in-app')
    }
  });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        notificationService.updateNotificationPreferences(
          buildPreferencesPayload()
        ),
        updateUserSettings({
          language: selectedLanguage,
          timezone: selectedTimezone
        })
      ]);
      // Update initial values to current values
      setInitialValues({
        userActionNotifications,
        informativeNotifications,
        alertNotifications,
        successNotifications,
        selectedLanguage,
        selectedTimezone
      });
      dispatch(setGeneralSettingsChanged(false));
      setToastText('Changes successfully saved!');
      setToastType('success');
      setShowToast(true);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to initial values
    setUserActionNotifications(initialValues.userActionNotifications);
    setInformativeNotifications(initialValues.informativeNotifications);
    setAlertNotifications(initialValues.alertNotifications);
    setSuccessNotifications(initialValues.successNotifications);
    setSelectedLanguage(initialValues.selectedLanguage);
    setSelectedTimezone(initialValues.selectedTimezone);
    dispatch(setGeneralSettingsChanged(false));
  };

  const handleConfigureTwoFactor = async () => {
    setIsTwoFactorUpdating(true);
    try {
      await userManager.signinRedirect({
        state: {
          returnTo: `${window.location.pathname}${window.location.search}`
        },
        extraQueryParams: {
          kc_action: 'CONFIGURE_TOTP'
        }
      });
    } catch (error) {
      console.error('Failed to start two-factor authentication setup:', error);
      setToastText('Failed to start two-factor authentication setup.');
      setToastType('error');
      setShowToast(true);
      setIsTwoFactorUpdating(false);
    }
  };

  const handleDisableTwoFactor = async () => {
    setIsTwoFactorUpdating(true);
    try {
      const updated = await disableMyTwoFactor();
      setTwoFactorStatus(updated);
      setSecurityError('');
      setShow2faModal(false);
      setToastText('Two-factor authentication disabled.');
      setToastType('success');
      setShowToast(true);
    } catch (error) {
      console.error('Failed to disable two-factor authentication:', error);
      setToastText('Failed to update two-factor authentication settings.');
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsTwoFactorUpdating(false);
    }
  };

  const hasTwoFactor = twoFactorStatus.twoFactorEnabled;

  const twoFactorModalTitle = hasTwoFactor
    ? 'Disable two-factor authentication'
    : 'Set up two-factor authentication';

  const twoFactorModalDescription = hasTwoFactor
    ? 'Disable two-factor authentication for your account? You will be able to sign in with password only until you set it up again.'
    : 'You will be redirected to the login page. Two-factor authentication setup will start after your next sign-in.';

  const twoFactorModalButtonText = hasTwoFactor
    ? isTwoFactorUpdating
      ? 'Disabling...'
      : 'Disable 2FA'
    : isTwoFactorUpdating
      ? 'Redirecting...'
      : 'Set up 2FA';

  const twoFactorMethods = formatTwoFactorMethods(
    twoFactorStatus.twoFactorMethods
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex-1 max-w-none w-full flex flex-col p-4 h-[calc(100vh_-_212px)]">
      <div className="overflow-y-auto">
        <div className="flex flex-col gap-5 px-4 py-2">
          {/* Preferences */}
          <div className="flex flex-col gap-6">
            <span className="flex flex-col gap-1 items-start">
              <h2 className="text-zinc-900 font-medium">Preferences</h2>
              <p className="text-sm text-gray-500">
                Set your language and time zone.
              </p>
            </span>
            <div className="flex gap-6">
              <div className="flex flex-col gap-6 w-1/2">
                <Dropdown
                  label="Language"
                  placeholder="Select language"
                  options={languageOptions}
                  value={selectedLanguage}
                  onChange={setSelectedLanguage}
                  disabled={isLoading}
                  error=""
                />
              </div>
              <div className="flex flex-col gap-6 w-1/2">
                <Dropdown
                  label="Timezone"
                  placeholder="Select timezone"
                  options={timezoneOptions}
                  value={selectedTimezone}
                  onChange={setSelectedTimezone}
                  disabled={isLoading}
                  error=""
                />
              </div>
            </div>
          </div>
          <Divider classes="my-3.5" />
          <div className="flex flex-col gap-6">
            <span className="flex flex-col gap-1 items-start">
              <h2 className="text-zinc-900 font-medium">
                Two-factor authentication
              </h2>
              <p className="text-sm text-gray-500">
                Add an authenticator app to help protect your account.
              </p>
            </span>
            <div className="flex items-center justify-between gap-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex flex-col gap-1 text-left">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900v2">
                    2FA status
                  </span>
                  {hasTwoFactor && (
                    <span className="rounded bg-cyan-50 px-2 py-0.5 text-xs font-medium text-cyan-950 ring-1 ring-cyan-300">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {isSecurityLoading
                    ? 'Checking status in Keycloak...'
                    : securityError ||
                      (hasTwoFactor
                        ? `Active methods: ${twoFactorMethods || '2FA'}`
                        : '2FA is not enabled for this account.')}
                </p>
              </div>

              {hasTwoFactor ? (
                <button
                  type="button"
                  onClick={() => setShow2faModal(true)}
                  disabled={isSecurityLoading || isTwoFactorUpdating}
                  className="inline-flex items-center justify-center rounded-[100px] border border-rose-200 bg-white px-[17px] py-[9px] text-[14px] font-medium leading-[20px] text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:border-gray-100 disabled:text-gray-300"
                >
                  {isTwoFactorUpdating ? 'Disabling...' : 'Disable 2FA'}
                </button>
              ) : (
                <TextButton
                  onClick={() => setShow2faModal(true)}
                  disabled={isSecurityLoading || isTwoFactorUpdating}
                >
                  Set up 2FA
                </TextButton>
              )}
            </div>
          </div>
          <Divider classes="my-3.5" />
          <div className="flex flex-col gap-6">
            <span className="flex flex-col gap-1 items-start">
              <h2 className="text-zinc-900 font-medium">Notifications</h2>
              <p className="text-sm text-gray-500">
                Set your notification preferences.
              </p>
            </span>
            <div className="text-left flex flex-col gap-4">
              <span className="text-sm font-semibold leading-5 not-italic  text-gray-900v2">
                User Action Notifications
              </span>
              <CheckboxGroup
                title="Notification channel"
                options={[
                  { value: 'in-app', label: 'In App' },
                  { value: 'email', label: 'Email' }
                ]}
                selectedValues={userActionNotifications}
                onChange={(value, checked) =>
                  handleNotificationChange(
                    value,
                    checked,
                    userActionNotifications,
                    setUserActionNotifications
                  )
                }
                requireAtLeastOne={true}
              />
            </div>
            <div className="text-left flex flex-col gap-4">
              <span className="text-sm font-semibold leading-5 not-italic  text-gray-900v2">
                Informative Notifications
              </span>
              <CheckboxGroup
                title="Notification channel"
                options={[
                  { value: 'in-app', label: 'In App' },
                  { value: 'email', label: 'Email' }
                ]}
                selectedValues={informativeNotifications}
                onChange={(value, checked) =>
                  handleNotificationChange(
                    value,
                    checked,
                    informativeNotifications,
                    setInformativeNotifications
                  )
                }
                requireAtLeastOne={true}
              />
            </div>
            <div className="text-left flex flex-col gap-4">
              <span className="text-sm font-semibold leading-5 not-italic  text-gray-900v2">
                Alert Notifications
              </span>
              <CheckboxGroup
                title="Notification channel"
                options={[
                  { value: 'in-app', label: 'In App' },
                  { value: 'email', label: 'Email' }
                ]}
                selectedValues={alertNotifications}
                onChange={(value, checked) =>
                  handleNotificationChange(
                    value,
                    checked,
                    alertNotifications,
                    setAlertNotifications
                  )
                }
                requireAtLeastOne={true}
              />
            </div>
            <div className="text-left flex flex-col gap-4">
              <span className="text-sm font-semibold leading-5 not-italic  text-gray-900v2">
                Success Notifications
              </span>
              <CheckboxGroup
                title="Notification channel"
                options={[
                  { value: 'in-app', label: 'In App' },
                  { value: 'email', label: 'Email' }
                ]}
                selectedValues={successNotifications}
                onChange={(value, checked) =>
                  handleNotificationChange(
                    value,
                    checked,
                    successNotifications,
                    setSuccessNotifications
                  )
                }
                requireAtLeastOne={true}
              />
            </div>
          </div>
        </div>
      </div>
      {showToast && (
        <Toast
          showToast={showToast}
          setShowToast={setShowToast}
          text={toastText}
          type={toastType}
        />
      )}
      {user.generalSettingsChanged && (
        <AccountSave
          handleSave={handleSave}
          handleCancel={handleCancel}
          disableSave={isSaving}
          buttonText={isSaving ? 'Saving...' : 'Save Changes'}
        />
      )}
      {show2faModal && (
        <Modal open={show2faModal} dialogPanelClassnames="w-[512px]">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h2 className="text-gray-900 text-lg leading-6 font-medium">
                {twoFactorModalTitle}
              </h2>
              <p className="text-sm text-gray-500">
                {twoFactorModalDescription}
              </p>
            </div>
            <div className="flex flex-row-reverse gap-3">
              <PrimaryButton
                onClick={
                  hasTwoFactor
                    ? handleDisableTwoFactor
                    : handleConfigureTwoFactor
                }
                disabled={isTwoFactorUpdating}
                className={`${hasTwoFactor ? 'text-white bg-rose-500 hover:bg-rose-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-rose-600 focus:bg-rose-500 active:ring-rose-600' : ''}`}
              >
                {twoFactorModalButtonText}
              </PrimaryButton>
              <WhiteButton
                onClick={() => setShow2faModal(false)}
                disabled={isTwoFactorUpdating}
              >
                Cancel
              </WhiteButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
